import { ref, computed, watch } from 'vue'

export function useMistakeReview({
  api,
  t,
  isMistakeReviewMode,
  isAssignedTestMode,
  isExamMode,
  selectedMode,
  selectedTopics
}) {
  const incorrectQuestionsCount = ref(0)
  const incorrectQuestionsLoading = ref(false)
  const mistakeReviewCountPreset = ref('20')

  const getDefaultMistakeReviewPreset = (count) => {
    if (count >= 20) return '20'
    return String(count)
  }

  /** Decrement mistake count when user answers correctly during review */
  const decrementIncorrectCount = () => {
    if (incorrectQuestionsCount.value > 0) {
      incorrectQuestionsCount.value--
    }
  }

  const loadIncorrectQuestionsSummary = async () => {
    incorrectQuestionsLoading.value = true

    try {
      const response = await api.get('/tests/incorrect-questions')
      incorrectQuestionsCount.value = response.data.count || 0
    } catch (error) {
      incorrectQuestionsCount.value = 0
    } finally {
      incorrectQuestionsLoading.value = false
    }
  }

  const mistakeReviewCountOptions = computed(() => {
    const count = incorrectQuestionsCount.value
    if (count <= 0) return []

    const options = []

    if (count >= 20) {
      options.push({ value: '20', label: '20' })
    }

    if (count >= 30) {
      options.push({ value: '30', label: '30' })
    }

    if (count >= 40) {
      options.push({ value: '40', label: '40' })
    }

    // 20-дан аз болса — нақты санды көрсету
    if (count < 20) {
      options.push({ value: String(count), label: String(count) })
    }

    return options
  })

  const resolvedMistakeQuestionCount = computed(() => {
    const availableCount = incorrectQuestionsCount.value
    if (availableCount <= 0) return 0

    if (mistakeReviewCountPreset.value === 'ALL') {
      return availableCount
    }

    const parsedCount = Number.parseInt(mistakeReviewCountPreset.value, 10)
    if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
      return Math.min(20, availableCount)
    }

    return Math.min(parsedCount, availableCount)
  })

  watch(isMistakeReviewMode, (enabled) => {
    if (!enabled) return

    isAssignedTestMode.value = false
    isExamMode.value = false
    selectedMode.value = 'TRAINING_MODE'
    selectedTopics.value = ['ALL_TOPICS']

    // Always reload from server when switching to mistake review mode
    // so new mistakes and correct answers are reflected immediately
    if (!incorrectQuestionsLoading.value) {
      loadIncorrectQuestionsSummary()
    }
  })

  watch(incorrectQuestionsCount, (count) => {
    const availableValues = mistakeReviewCountOptions.value.map(option => option.value)
    if (!availableValues.length) {
      mistakeReviewCountPreset.value = String(count || 0)
      return
    }

    if (!availableValues.includes(mistakeReviewCountPreset.value)) {
      mistakeReviewCountPreset.value = getDefaultMistakeReviewPreset(count)
    }
  }, { immediate: true })

  return {
    incorrectQuestionsCount,
    incorrectQuestionsLoading,
    mistakeReviewCountPreset,
    mistakeReviewCountOptions,
    resolvedMistakeQuestionCount,
    loadIncorrectQuestionsSummary,
    decrementIncorrectCount
  }
}
